import { createElement, useMemo, type ReactNode } from 'react'
import { Trash2 } from 'lucide-react'
import type { StrategicPointData } from '@/pages/StrategicPointsMap'
import { POINT_ICONS, pointIconLabel } from './pointIcons'
import { useAuth, isOperator } from '@/contexts/AuthContext'
import { useLanguage } from '@/contexts/LanguageContext'
import { CALLOUT_COLOR, type Callout } from '@/lib/callouts'

/**
 * Haritadaki noktaların listesi.
 *
 * Bölümün kuralı burada görünür hale gelir: **herkes ekler, kimse başkasının
 * noktasını silmez** (yönetim hariç). Silme düğmesi bu yüzden satır başına
 * basılır, listenin tamamına değil — ve kendi satırların "Senin" rozetiyle
 * işaretlenir, çünkü kalabalık bir katmanda hangisinin senin olduğunu bilmek
 * hangisini silebileceğini bilmekle aynı şey.
 *
 * Mevkiler aynı kuralla ikinci bölümde listelenir. Onlarda silme hakkını
 * backend'in `can_edit`'i söyler; noktaların yanıtı bu alanı taşımadığı için
 * orada kural hâlâ burada birleştiriliyor.
 */

interface Props {
  points: StrategicPointData[]
  selectedPointId: number | null
  onSelectPoint: (id: number | null) => void
  onDeletePoint: (id: number) => void
  loading: boolean
  callouts: Callout[]
  selectedCalloutId: number | null
  onSelectCallout: (id: number | null) => void
  onDeleteCallout: (id: number) => void
}

/** Liste içi alt başlık: iki katman aynı kaydırmada durur. */
function SubHeading({ children, count }: { children: ReactNode; count: number | string }) {
  return (
    <div className="sticky top-0 z-[1] flex items-center gap-2 border-b border-white/10 bg-black/60 px-4 py-2 backdrop-blur-sm">
      <span className="flex-1 truncate font-display text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground/70">
        {children}
      </span>
      <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground/50">{count}</span>
    </div>
  )
}

function formatDate(ts: number): string {
  if (!ts) return '-'
  return new Date(ts * 1000).toLocaleString('tr-TR', {
    day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
  })
}

export default function StrategicPointsSidebar({
  points,
  selectedPointId,
  onSelectPoint,
  onDeletePoint,
  loading,
  callouts,
  selectedCalloutId,
  onSelectCallout,
  onDeleteCallout,
}: Props) {
  const { T } = useLanguage()
  const { user } = useAuth()
  const staff = isOperator(user)

  // Sıralama listenin kendisinden gelir (oluşturma sırası); burada yalnızca
  // "benim mi" bilgisi ekleniyor.
  const rows = useMemo(
    () => points.map(p => ({ p, mine: !!user && p.created_by === user.id })),
    [points, user],
  )

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Başlık — üst çubukla aynı 68px'lik hat (bkz. StrategicPointsMap) */}
      <div className="flex h-[68px] shrink-0 items-center gap-2 border-b border-white/10 px-4">
        <span aria-hidden className="h-px w-4 shrink-0 bg-primary" />
        <span className="flex-1 truncate font-display text-[11px] font-bold uppercase tracking-[0.18em] text-foreground/70">
          {T.strategicPoints.title}
        </span>
      </div>

      {/* Liste */}
      <div className="flex-1 overflow-y-auto pb-1">
        <SubHeading count={loading ? '' : points.length}>{T.strategicPoints.countLabel}</SubHeading>
        {loading && points.length === 0 &&
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="px-4 py-3 border-b border-white/10 space-y-2 animate-pulse">
              <div className="h-3 w-16 rounded bg-white/8" />
              <div className="h-4 w-3/4 rounded bg-white/8" />
            </div>
          ))
        }
        {!loading && points.length === 0 && (
          <p className="px-4 py-4 text-xs text-muted-foreground">{T.strategicPoints.empty}</p>
        )}

        {rows.map(({ p, mine }) => {
          const isSelected = p.id === selectedPointId
          const canDelete = staff || mine
          return (
            <div
              key={p.id}
              className={`flex cursor-pointer items-center gap-2 border-b border-l-2 border-white/5 px-3 py-2.5 transition-colors hover:bg-white/5
                ${isSelected ? 'border-l-primary bg-primary/10' : 'border-l-transparent'}`}
              onClick={() => onSelectPoint(isSelected ? null : p.id)}
            >
              {/* Simge */}
              <div className="shrink-0 w-4 h-4 flex items-center justify-center" style={{ color: p.color || '#ef4444' }}>
                {createElement((POINT_ICONS[p.icon_type] || POINT_ICONS.other).icon, { className: 'h-3.5 w-3.5' })}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">
                  {p.label || pointIconLabel(p.icon_type, T)}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-muted-foreground/60">
                  <span className="truncate">{p.created_by_name}</span>
                  {mine && (
                    <span className="shrink-0 rounded-sm bg-primary/20 px-1 text-[9px] font-semibold uppercase tracking-wider text-primary">
                      {T.strategicPoints.yours}
                    </span>
                  )}
                  <span className="ml-auto shrink-0 tabular-nums">{formatDate(p.created_at)}</span>
                </div>
              </div>
              {/* Silme — yalnızca sahibine ve yönetime */}
              {canDelete && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm(T.strategicPoints.panel.confirmDelete)) onDeletePoint(p.id)
                  }}
                  className="shrink-0 p-1 rounded text-muted-foreground/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title={T.strategicPoints.panel.deletePoint}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )
        })}

        {/* ── Mevkiler ─────────────────────────────────────────── */}
        <SubHeading count={callouts.length}>{T.callouts.title}</SubHeading>
        {callouts.length === 0 && (
          <p className="px-4 py-4 text-xs text-muted-foreground">{T.callouts.empty}</p>
        )}
        {callouts.map(c => {
          const isSelected = c.id === selectedCalloutId
          const mine = !!user && c.created_by === user.id
          return (
            <div
              key={c.id}
              className={`flex cursor-pointer items-center gap-2 border-b border-l-2 border-white/5 px-3 py-2.5 transition-colors hover:bg-white/5
                ${isSelected ? 'border-l-primary bg-primary/10' : 'border-l-transparent'}`}
              onClick={() => onSelectCallout(isSelected ? null : c.id)}
            >
              <div className="flex h-4 w-4 shrink-0 items-center justify-center">
                <span
                  aria-hidden
                  className="block h-3 w-3 rounded-[2px] border-[1.5px]"
                  style={{ borderColor: CALLOUT_COLOR, background: `${CALLOUT_COLOR}40` }}
                />
              </div>
              <div className="flex-1 min-w-0">
                {/* Mevki adı veridir, büyütülmez. */}
                <p className="text-sm font-semibold truncate">{c.name}</p>
                <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-muted-foreground/60">
                  <span className="truncate">{c.created_by_name}</span>
                  {mine && (
                    <span className="shrink-0 rounded-sm bg-primary/20 px-1 text-[9px] font-semibold uppercase tracking-wider text-primary">
                      {T.strategicPoints.yours}
                    </span>
                  )}
                  <span className="ml-auto shrink-0 tabular-nums">{formatDate(c.created_at)}</span>
                </div>
              </div>
              {c.can_edit && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    if (confirm(T.callouts.confirmDelete)) onDeleteCallout(c.id)
                  }}
                  className="shrink-0 p-1 rounded text-muted-foreground/30 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                  title={T.callouts.delete}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
